import { motion } from "framer-motion";
import IMG, { isGeneratedRender } from "@/lib/images";
import RenderedBadge from "@/components/RenderedBadge";

const CARDS = [
  {
    title: "Jadau Design Intelligence",
    desc: "Understands embedded stone settings, gold-thread cradles and the layered weight of authentic Mughal-era jadau.",
    image: IMG.jadau,
    tag: "Heritage Craft",
  },
  {
    title: "Polki Stone Layout",
    desc: "Generates uncut-diamond compositions with realistic foil-backing, irregular silhouettes and bridal proportions.",
    image: IMG.polki,
    tag: "Bridal Statement",
  },
  {
    title: "Meenakari Texture Rendering",
    desc: "Photoreal enamel finishes in peacock blue, ruby red and emerald, faithful to Jaipur and Banaras schools.",
    image: IMG.meenakari,
    tag: "Enamel Detail",
  },
  {
    title: "Temple Jewellery Motifs",
    desc: "Granulated gold, Lakshmi & Kasu motifs, and South Indian bridal layering generated to scale.",
    image: IMG.temple,
    tag: "South Indian",
  },
  {
    title: "Indian Gold Filigree",
    desc: "Hairline tarakashi filigree, paisley & vine motifs rendered with manufacturing-aware geometry.",
    image: IMG.filigree,
    tag: "Fine Craft",
  },
  {
    title: "Rajwadi Bridal Language",
    desc: "Multi-strand chokers, matha patti, baajubandh — composed in the regal Rajwadi vocabulary.",
    image: IMG.bridal,
    tag: "Bridal Couture",
  },
];

export default function Intelligence() {
  return (
    <section id="intelligence" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
            Indian Jewellery Intelligence
          </p>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-white">
            An AI that <span className="italic gold-gradient-text">speaks the craft</span> — not just renders it.
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg font-light leading-relaxed max-w-2xl">
            Trained on the visual grammar of Indian ateliers — every motif, every layering convention, every
            structural rule that makes traditional jewellery <em>wearable</em>, not just beautiful.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {CARDS.map((c, i) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              data-testid={`intelligence-card-${i}`}
              className="glass-card rounded-2xl overflow-hidden group"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  style={{ backgroundImage: `url(${c.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/30 to-transparent" />
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] bg-black/40 backdrop-blur px-2.5 py-1 rounded-full border border-[#D4AF37]/30">
                  {c.tag}
                </span>
                {isGeneratedRender(c.image) && (
                  <RenderedBadge className="absolute top-3 right-3" />
                )}
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl md:text-2xl text-white tracking-tight">{c.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{c.desc}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs text-[#D4AF37]/80 group-hover:text-[#D4AF37] transition-colors">
                  Explore capability
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
